import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const PRODUCTS = [
  {
    id: 1,
    title: "Balance",
    subtitle: "Nourishing Serum",
    image: "https://cdn.jiro.build/Natural%20Glow/Images/1st%20image.png",
  },
  {
    id: 2,
    title: "Radiance",
    subtitle: "Hydrating Cream",
    image: "https://cdn.jiro.build/Natural%20Glow/Images/2nd%20image.png",
  },
  {
    id: 3,
    title: "Harmony",
    subtitle: "Gentle Cleanser",
    image: "https://cdn.jiro.build/Natural%20Glow/Images/3rd%20image.png",
  },
  {
    id: 4,
    title: "Renewal",
    subtitle: "Botanical Facial Oil",
    image: "https://cdn.jiro.build/Natural%20Glow/Images/4th%20image.png",
  },
];

export default function ProductsListNatural({ className }) {
  const sectionRef = useRef(null);
  const cardsContainerRef = useRef(null);
  const rightTextRef = useRef(null);
  const wordsStackRef = useRef(null);

  useEffect(() => {
    const sectionEl = sectionRef.current;
    const cardsEl = cardsContainerRef.current;
    const rightTextEl = rightTextRef.current;
    const wordsStackEl = wordsStackRef.current;

    if (!sectionEl || !cardsEl || !rightTextEl || !wordsStackEl) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionEl,
        start: "top top",
        end: "+=320%",
        pin: true,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      },
    });

    tl.fromTo(
      rightTextEl,
      { y: "0vh" },
      { y: "0vh", ease: "none", duration: 0.8 },
      0
    );

    tl.fromTo(
      cardsEl,
      { y: 0 },
      { y: -1356, ease: "none", duration: 3.6 },
      0
    );

    tl.to(
      wordsStackEl,
      { y: "-25%", ease: "power2.inOut", duration: 0.6 },
      0.9
    );

    tl.to(
      wordsStackEl,
      { y: "-50%", ease: "power2.inOut", duration: 0.6 },
      1.8
    );

    tl.to(
      wordsStackEl,
      { y: "-75%", ease: "power2.inOut", duration: 0.6 },
      2.7
    );

    return () => {
      tl.scrollTrigger?.kill();
      tl.kill();
    };
  }, []);

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link
        href="https://fonts.googleapis.com/css2?family=Big+Shoulders+Display:wght@400;500;600;700&display=swap"
        rel="stylesheet"
      />

      <div
        ref={sectionRef}
        className={"relative w-full h-screen bg-[#4D5B44] text-white overflow-hidden flex items-center justify-center px-4 md:px-12 " + (className || "")}
      >
        {/* MIDDLE SCROLLABLE BOXES COLUMN */}
        <div className="relative z-10 w-full max-w-[540px] h-screen flex justify-center items-start overflow-hidden">
          <div
            ref={cardsContainerRef}
            className="flex flex-col gap-8 items-center w-full pt-[calc(50vh-210px)] pb-[calc(50vh-210px)]"
          >
            {PRODUCTS.map((prod) => (
              <div
                key={prod.id}
                className="w-[280px] sm:w-[380px] md:w-[440px] h-[360px] sm:h-[420px] bg-[#718265] rounded-[16px] overflow-hidden relative shadow-lg group cursor-pointer shrink-0 transition-transform hover:scale-[1.02] flex items-center justify-center p-3 sm:p-4"
              >
                <img
                  src={prod.image}
                  alt={prod.title}
                  referrerPolicy="no-referrer"
                  className="w-full h-full object-contain object-center rounded-[12px] opacity-95 group-hover:opacity-100 transition-opacity"
                />
                <div className="absolute bottom-4 left-4 right-4 bg-[#4D5B44]/80 backdrop-blur-md rounded-[12px] p-3 text-white z-20">
                  <span className="block text-xs uppercase tracking-wider text-[#B9F33F] font-semibold">
                    Product 0{prod.id}
                  </span>
                  <p className="text-sm font-medium">{prod.subtitle}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* TOP OVERLAY: SYMMETRIC 3-COLUMN GRID */}
        <div className="absolute top-[20px] lg:top-[40px] left-0 right-0 z-20 px-3 sm:px-6 lg:px-10 grid grid-cols-[1fr_auto_1fr] items-start gap-2 md:gap-6 lg:gap-8 pointer-events-none">
          {/* LEFT COLUMN: "Natural" */}
          <div className="flex justify-start items-start overflow-visible pointer-events-auto">
            <h2
              className="tracking-tighter block whitespace-nowrap"
              style={{
                color: "#FFFFFF",
                fontFamily: '"Big Shoulders Display", sans-serif',
                fontSize: "clamp(32px, 8.2vw, 165px)",
                fontStyle: "normal",
                fontWeight: 700,
                lineHeight: "0.95",
                letterSpacing: "-0.02em",
                margin: 0,
                paddingLeft: "2px",
              }}
            >
              Natural
            </h2>
          </div>

          {/* CENTER SPACER */}
          <div className="w-[280px] sm:w-[380px] md:w-[440px] shrink-0" />

          {/* RIGHT COLUMN: WORDS STACK */}
          <div
            ref={rightTextRef}
            className="flex justify-end items-start overflow-hidden text-right pointer-events-auto px-1"
            style={{
              height: "clamp(30px, 7.8vw, 156px)",
            }}
          >
            <div ref={wordsStackRef} className="flex flex-col items-end">
              {PRODUCTS.map((prod) => (
                <h2
                  key={prod.id}
                  className="tracking-tighter block whitespace-nowrap"
                  style={{
                    color: "#FFFFFF",
                    textAlign: "right",
                    fontFamily: '"Big Shoulders Display", sans-serif',
                    fontSize: "clamp(32px, 8.2vw, 165px)",
                    fontStyle: "normal",
                    fontWeight: 700,
                    lineHeight: "0.95",
                    letterSpacing: "-0.02em",
                    margin: 0,
                    paddingRight: "2px",
                    height: "clamp(30px, 7.8vw, 156px)",
                  }}
                >
                  {prod.title}
                </h2>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}